import { Typography, Card, Tag, Row, Col, Radio, Space } from "antd";
import type { FmvAPI } from "../fmv/FmvTableComponent";
import { TfsPartnerView } from "./TfsPartnerView";
import { DefaultPartnerView } from "./DefaultPartnerView";
import type {
  LowDpCalculationResponse,
  MonthlyWithAmount,
} from "../../helpers/interfaces/CalculatorInterfaces";

const { Text, Title } = Typography;

type PartnerViewTableProps = {
  unitType: "ACV" | "Repoz";
  unitPrice: number;
  downpayment: number;
  downpaymentPercent: number;
  financedAmount: number;
  providerName: string;
  vehicle: FmvAPI | null;
  isTfs: boolean;
  calculationResult: any;
  monthlyTerms: any[];
  selectedTermIndex: number;
  onSelectTerm: (index: number) => void;
  isDirty?: boolean;
};

export const currencyFormat = (value?: number) => {
  if (!value) {
    return '-';
  }

  return `₱${value.toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const TERM_LABELS: Record<number, string> = {
  12: "1 Year",
  24: "2 Years",
  36: "3 Years",
  48: "4 Years",
  60: "5 Years",
  72: "6 Years",
  84: "7 Years",
};

const labelFromMonths = (months: number) =>
  TERM_LABELS[months] || `${months} Months`;

// ── Shared Cell Styles ──
const cellLabelStyle: React.CSSProperties = {
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "12px",
  color: "#475569",
  borderBottom: "1px solid #d6dee8",
  borderRight: "1px solid #d6dee8",
  width: "50%",
};

const cellValueStyle: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: "13px",
  color: "#1e293b",
  fontWeight: 500,
  borderBottom: "1px solid #d6dee8",
  width: "50%",
};

// ── Main Export ──
export const PartnerViewTable = ({
   unitType,
    unitPrice,
    downpayment,
    downpaymentPercent,
    financedAmount,
    providerName,
    vehicle,
    isTfs,
    calculationResult,
    monthlyTerms,
    selectedTermIndex,
    onSelectTerm,
    isDirty,
}: PartnerViewTableProps) => {
  console.log(calculationResult, 'calculationResult');

  // Empty state — no calculation result yet
  if (!calculationResult || monthlyTerms.length === 0) {
    return (
      <Card
        style={{
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          textAlign: "center",
          padding: "48px 24px",
        }}
      >
        <Text type="secondary" style={{ fontSize: "15px" }}>
          {!calculationResult
            ? "Fill in the inputs and click Calculate to generate the partner breakdown."
            : "No terms available in the calculation result."}
        </Text>
      </Card>
    );
  }

  const selectedMonth = monthlyTerms[selectedTermIndex] || monthlyTerms[0];
  const partnerView = selectedMonth?.partner_view;
  const calcParams = calculationResult.calculation_params;

  const headerGradient =
    unitType === "ACV"
      ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
      : "linear-gradient(135deg, #3d1500 0%, #d4380d 100%)";

  const tagColor = unitType === "ACV" ? "purple" : "volcano";

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        overflow: "hidden",
        position: "relative",
      }}
      bodyStyle={{ padding: 0 }}
    >
      {isDirty && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(4px)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ background: "#fff", padding: "16px 24px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #d6dee8" }}>
            <Text strong style={{ color: "#d4380d", fontSize: "15px" }}>
              Parameter has been changed, click the calculate button to load up new computations
            </Text>
          </div>
        </div>
      )}
      {/* Header */}
      <div
        style={{
          background: headerGradient,
          padding: "24px 32px",
          color: "white",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Text
              style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Automart Calculation Request
            </Text>
            <Title level={5} style={{ color: "white", margin: "4px 0 0", fontWeight: 700 }}>
              {vehicle
                ? `${vehicle.brand.toUpperCase()} ${vehicle.model.toUpperCase()} ${vehicle.trim.toUpperCase()} ${vehicle.model_year} ${vehicle.transmission.toUpperCase()} ${vehicle.location.toUpperCase()} - ${vehicle.bmtytl_code}`
                : `${providerName.toUpperCase()} — Direct Calculation`}
            </Title>
            <Text style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px" }}>
              For partner submission — {providerName}
            </Text>
          </div>
          <Tag
            color={tagColor}
            style={{
              fontSize: "12px",
              padding: "4px 12px",
              borderRadius: "6px",
              fontWeight: 600,
            }}
          >
            {unitType}
          </Tag>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 32px" }}>
        {/* Summary Row (SRP / DP / Financed) */}
        {monthlyTerms.length > 1 && (
          <div style={{ marginBottom: "24px" }}>
            <Text
              strong
              style={{
                display: "block",
                marginBottom: "12px",
                color: "#64748b",
                textTransform: "uppercase",
                fontSize: "11px",
                letterSpacing: "1px",
              }}
            >
              All Available Terms
            </Text>
            <Row gutter={[12, 12]}>
              {monthlyTerms.map((term, idx) => {
                console.log(term, 'term')
                const isActive = idx === selectedTermIndex;
                return (
                  <Col xs={24} sm={8} key={idx}>
                    <div
                      onClick={() => onSelectTerm(idx)}
                      style={{
                        padding: "16px",
                        borderRadius: "10px",
                        border: isActive ? "2px solid #334155" : "2px solid #d6dee8",
                        background: isActive
                          ? "linear-gradient(135deg, #f0f2ff 0%, #e8eaff 100%)"
                          : "#fbfcfd",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        textAlign: "center",
                      }}
                    >
                      <Text
                        strong
                        style={{
                          display: "block",
                          fontSize: "13px",
                          color: "#1e293b",
                          marginBottom: "4px",
                        }}
                      >
                        {labelFromMonths(term.months_to_pay)}
                      </Text>
                      <Text
                        style={{
                          fontSize: "11px",
                          color: "#64748b",
                          display: "block",
                        }}
                      >
                        Rate: {((term.interest_rate || term.interest_value) * 100).toFixed(2)}%
                      </Text>
                      <Text
                        strong
                        style={{
                          fontSize: "18px",
                          color: isActive ? "#f97316" : "#1e293b",
                          display: "block",
                          marginTop: "8px",
                        }}
                      >
                        {currencyFormat(term.monthly_amount)}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px" }}
                      >
                        /month
                      </Text>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
        {/* Partner View Section */}
        {partnerView && (
          <div>
            <Text
              strong
              style={{
                display: "block",
                marginBottom: "12px",
                color: "#64748b",
                textTransform: "uppercase",
                fontSize: "11px",
                letterSpacing: "1px",
              }}
            >
              Partner View — {labelFromMonths(selectedMonth.months_to_pay)}
            </Text>
            {isTfs ? (
              <TfsPartnerView 
                calculationResult={calculationResult}
                selectedMonth={selectedMonth}
                partnerView={partnerView}
              />
            ) : (
              <DefaultPartnerView 
                selectedMonth={selectedMonth}
                partnerView={partnerView}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
